import { reportConsentDecision } from "./beacon"
import {
  CONSENT_VERSION,
  type ConsentDecision,
  type ConsentSource,
  GLOBAL_COOKIE_BANNER_CLASSNAME,
  STORAGE_CONSENT_KEY,
} from "./constants"

/** Same-tab counterpart to `storage`, which only fires in other tabs. */
export const CONSENT_CHANGE_EVENT = "tt.storage"

function readStoredDecision(): ConsentDecision | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_CONSENT_KEY)

    if (stored === `${CONSENT_VERSION}-accepted`) {
      return "accepted"
    }

    if (stored === `${CONSENT_VERSION}-rejected`) {
      return "rejected"
    }

    return null
  } catch {
    return null
  }
}

/**
 * Non-essential cookies are allowed until the visitor explicitly rejects them.
 * Gate PostHog, GTM and every other non-essential tag on this.
 *
 * Returns false while rendering on the server, where localStorage is
 * unreadable — pair it with `isResolved` from `useConsent` before treating a
 * false as a rejection.
 */
/**
 * Whether the visitor has chosen at all, either way. Distinct from
 * `isPerformanceAllowed`, which is true for someone who has never chosen. Use
 * this to hold back UI that would otherwise sit on top of the banner.
 */
export function hasStoredDecision(): boolean {
  if (typeof window === "undefined") {
    return false
  }

  return readStoredDecision() !== null
}

export function isPerformanceAllowed(): boolean {
  if (typeof window === "undefined") {
    return false
  }

  return readStoredDecision() !== "rejected"
}

export function setConsentDecision(decision: ConsentDecision, source: ConsentSource): void {
  const value = `${CONSENT_VERSION}-${decision}`

  try {
    window.localStorage.setItem(STORAGE_CONSENT_KEY, value)
  } catch {
    // Private-mode browsers reject writes; the banner still closes for this page view.
  }

  document.body.classList.remove(GLOBAL_COOKIE_BANNER_CLASSNAME)
  window.dispatchEvent(
    new CustomEvent(CONSENT_CHANGE_EVENT, {
      detail: { key: STORAGE_CONSENT_KEY, newValue: value },
    }),
  )

  reportConsentDecision(decision, source)

  /* A reject has to stop tags that are already running, and unmounting them only
     removes the script — GTM's runtime and PostHog's capture both survive in the
     current document. Reloading is the one mechanism that stops everything: the
     fresh document reads the stored rejection and starts neither. The beacon
     above is sent so it survives the unload. */
  if (decision === "rejected") {
    window.location.reload()
  }
}

/**
 * Runs before paint so an undecided visitor sees the banner without a flash.
 * Inline it in `_document` — the banner stays hidden until this class lands.
 */
export function generateCookieBannerScript(): string {
  function cookieBannerScript(consentKey: string, bannerClassName: string, version: number) {
    try {
      const consent = window.localStorage.getItem(consentKey)
      if (consent !== `${version}-accepted` && consent !== `${version}-rejected`) {
        document.body.classList.add(bannerClassName)
      }
    } catch {}
  }

  return `(${cookieBannerScript.toString()})(${JSON.stringify(
    STORAGE_CONSENT_KEY,
  )}, ${JSON.stringify(GLOBAL_COOKIE_BANNER_CLASSNAME)}, ${JSON.stringify(CONSENT_VERSION)})`
}
